import { InlineExpression as Base } from "@corca/tiptap";
import { NodeSelection, Plugin, PluginKey } from "@tiptap/pm/state";
import { ReplaceAroundStep, ReplaceStep } from "@tiptap/pm/transform";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { debounce } from "es-toolkit";

import { createMathMlAnnotation } from "@/components/expression/MathMlAnnotation";
import { ExpressionNodeRenderer } from "@/components/tiptap/tiptap-expression/ExpressionNodeRenderer";
import { posIsInViewport } from "@/utils/posIsInViewport";
import { scheduleOnFrame } from "@/utils/scheduleOnFrame";

import { createWrapperNode, stopTiptapEvent } from "./utils";

import type { ExpressionNodeExtensionOptions } from "./types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import type { NodeId, WrapperNodeConstructorProps } from "@corca/data";
import type { InlineNodeAttrs } from "@corca/tiptap";
import type { Node as PMNode } from "@tiptap/pm/model";
import type { Transaction } from "@tiptap/pm/state";
import type { NodeView } from "@tiptap/pm/view";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    inlineExpression: {
      insertInlineExpression: (
        inlineNode?: InlineNodeModel,
        pos?: number,
        props?: WrapperNodeConstructorProps,
      ) => ReturnType;
      updateInlineExpression: (pos: number, attrs: Partial<InlineNodeAttrs>) => ReturnType;
    };
  }
}

const NEAR_CURSOR_THRESHOLD = 100;
const SCROLL_HANDLER_DEBOUNCE = 2000;

export const lazyPluginKey = new PluginKey("lazyPlugin");

type LazyDecSpec = { eager?: boolean };

/**
 * Puts the expression's screen-reader annotation beside its node view;
 * the rendered one is a React portal, whose element screen readers announce as "clickable".
 */
function withMathMlAnnotation(view: NodeView, node: PMNode, doc: DocumentModel): NodeView {
  let nodeId = node.attrs.nodeId as NodeId;
  let annotation = createMathMlAnnotation(doc, nodeId);
  const dom = document.createElement("span");
  dom.append(annotation.element, view.dom);

  return {
    dom,
    update: (newNode, decorations, innerDecorations) => {
      const updated = view.update?.(newNode, decorations, innerDecorations) ?? false;
      const newNodeId = newNode.attrs.nodeId as NodeId;

      if (updated && newNodeId !== nodeId) {
        const previous = annotation;
        nodeId = newNodeId;
        annotation = createMathMlAnnotation(doc, nodeId);
        previous.element.replaceWith(annotation.element);
        previous.destroy();
      }
      return updated;
    },
    selectNode: view.selectNode?.bind(view),
    deselectNode: view.deselectNode?.bind(view),
    stopEvent: view.stopEvent?.bind(view),
    ignoreMutation: view.ignoreMutation?.bind(view),
    destroy: () => {
      view.destroy?.();
      annotation.destroy();
    },
  };
}

function getIsNodeCursored(nodeId: NodeId, doc: DocumentModel): boolean {
  const wrapperNode = nodeId && doc ? doc.nodes.get(nodeId) : undefined;
  return !!wrapperNode && !!doc?.cursor.isOnNode(wrapperNode);
}

/**
 * Whether a step lands an expression somewhere, which is how a line move or a remote edit
 * reorders equations without touching the node models.
 */
function insertsInlineExpression(tr: Transaction): boolean {
  return tr.steps.some((step) => {
    if (!(step instanceof ReplaceStep || step instanceof ReplaceAroundStep)) return false;

    let found = false;
    step.slice.content.descendants((node) => {
      if (node.type.name === "inlineExpression") found = true;
      return !found;
    });
    return found;
  });
}

export const TiptapInlineExpression = Base.extend<
  ExpressionNodeExtensionOptions,
  {
    observer?: IntersectionObserver;
    scrollCleanup?: () => void;
    getPosMap: Map<Element, () => number | undefined>;
  }
>({
  addStorage() {
    return {
      getPosMap: new Map(),
    };
  },

  onTransaction({ transaction }) {
    if (insertsInlineExpression(transaction)) {
      this.options.doc.computedNodes.markLayoutChanged();
    }
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: lazyPluginKey,

        state: {
          init() {
            return DecorationSet.empty;
          },
          apply(tr, prevDecSet) {
            let decSet = prevDecSet.map(tr.mapping, tr.doc);

            const meta = tr.getMeta(lazyPluginKey) as {
              addEager?: number[];
              removeEager?: number[];
            };
            if (!meta) {
              return decSet;
            }

            const addDecs: Decoration[] = [];
            const removeDecs: Decoration[] = [];

            if (meta.addEager) {
              for (const pos of meta.addEager) {
                if (pos < tr.doc.content.size) {
                  let dec = decSet.find(pos, pos, (spec: LazyDecSpec) => "eager" in spec)[0];
                  if ((dec?.spec as LazyDecSpec)?.eager !== true) {
                    if (dec) {
                      removeDecs.push(dec);
                    }
                    dec = Decoration.node(
                      pos,
                      pos + 1,
                      {},
                      {
                        eager: true,
                      },
                    );
                    addDecs.push(dec);
                  }
                }
              }
            }

            if (meta.removeEager) {
              for (const pos of meta.removeEager) {
                if (pos < tr.doc.content.size) {
                  let dec = decSet.find(pos, pos, (spec: LazyDecSpec) => "eager" in spec)[0];
                  if ((dec?.spec as LazyDecSpec)?.eager !== false) {
                    if (dec) {
                      removeDecs.push(dec);
                    }
                    dec = Decoration.node(
                      pos,
                      pos + 1,
                      {},
                      {
                        eager: false,
                      },
                    );
                    addDecs.push(dec);
                  }
                }
              }
            }

            if (addDecs.length > 0) {
              decSet = decSet.add(tr.doc, addDecs);
            }

            if (removeDecs.length > 0) {
              decSet = decSet.remove(removeDecs);
            }

            return decSet;
          },
        },

        props: {
          decorations(state) {
            return this.getState(state);
          },
        },
      }),
    ];
  },

  addNodeView() {
    const { doc, alwaysEager } = this.options;

    if (!this.storage.observer) {
      this.storage.observer = new IntersectionObserver(
        (entries) => {
          const addQueue: number[] = [];

          for (const entry of entries) {
            if (entry.isIntersecting && entry.target.classList.contains("expr-placeholder")) {
              const pos = this.storage.getPosMap.get(entry.target)?.();
              if (pos !== undefined) {
                addQueue.push(pos);
              }
            }
          }

          if (addQueue.length > 0) {
            const { state, view } = this.editor;
            const { tr } = state;
            tr.setMeta(lazyPluginKey, { addEager: addQueue });
            view.dispatch(tr);
          }
        },
        {
          rootMargin: "100% 0px",
        },
      );
    }

    if (!this.storage.scrollCleanup) {
      const demoteDistantEager = () => {
        const removeQueue: number[] = [];
        const viewportHeight = window.innerHeight;
        const threshold = viewportHeight * 2;

        this.storage.getPosMap.forEach((getPos, el) => {
          if (el.classList.contains("expr-placeholder")) return;

          const rect = el.getBoundingClientRect();
          if (rect.bottom < -threshold || rect.top > viewportHeight + threshold) {
            const pos = getPos();
            if (pos === undefined) return;

            const node = this.editor.state.doc.nodeAt(pos);
            if (!node) return;

            if (!getIsNodeCursored(node.attrs.nodeId as NodeId, doc)) {
              removeQueue.push(pos);
            }
          }
        });

        if (removeQueue.length > 0) {
          const { state, view } = this.editor;
          const { tr } = state;
          tr.setMeta(lazyPluginKey, { removeEager: removeQueue });
          view.dispatch(tr);
        }
      };

      const handleScroll = debounce(demoteDistantEager, SCROLL_HANDLER_DEBOUNCE);
      document.addEventListener("scroll", handleScroll, { capture: true, passive: true });

      this.storage.scrollCleanup = () => {
        document.removeEventListener("scroll", handleScroll, { capture: true });
        handleScroll.cancel();
      };
    }

    const reactRenderer = ReactNodeViewRenderer(ExpressionNodeRenderer, {
      attrs: {
        // Prevent screen readers from reading the node view as a clickable element.
        // It does not affect navigation in focus mode as we handle it ourselves.
        role: "none",
      },
      stopEvent: ({ event }) => {
        return stopTiptapEvent(event, doc);
      },
    });

    return (props) => {
      const { node, getPos, decorations, view } = props;

      const { storage } = this;

      const eagerDec = decorations.find((d) => "eager" in d.spec);
      let eager = (eagerDec?.spec as LazyDecSpec)?.eager === true;

      const isActiveExpression = getIsNodeCursored(node.attrs.nodeId as NodeId, doc);

      const pos = getPos();

      if (pos !== undefined) {
        const { selection } = view.state;
        const cursorPos = selection.head;

        if (Math.abs(pos - cursorPos) <= NEAR_CURSOR_THRESHOLD) {
          eager = true;
        }

        if (!eager && !eagerDec) {
          // reduce flashing on undo by checking if the node is likely in viewport
          // there will be false positives if part of restored content is outside viewport, but that's acceptable
          eager = posIsInViewport(pos, view);
        }
      }

      if (isActiveExpression || alwaysEager) {
        eager = true;
      }

      if (eager || !window.IntersectionObserver) {
        const rendered = reactRenderer(props);

        const { dom } = rendered;
        if (dom instanceof Element) {
          storage.getPosMap.set(dom, getPos);
        }

        const origDestroy = rendered.destroy;

        rendered.destroy = () => {
          origDestroy?.call(rendered);
          if (dom instanceof Element) {
            storage.getPosMap.delete(dom);
          }
        };

        const origUpdate = rendered.update;

        rendered.update = (newNode, dec, innerDec) => {
          if (newNode.type !== node.type) {
            return false;
          }

          const isActiveNext = getIsNodeCursored(newNode.attrs.nodeId as NodeId, doc);

          const isNotEager = dec.some((d) => (d.spec as LazyDecSpec).eager === false);
          if (isNotEager && !isActiveNext) {
            return false;
          }

          return origUpdate ? origUpdate.call(rendered, newNode, dec, innerDec) : true;
        };

        return withMathMlAnnotation(rendered, node, doc);
      }

      const dom = document.createElement("span");

      storage.getPosMap.set(dom, getPos);

      const updatePlaceholderStyles = (attrs: InlineNodeAttrs) => {
        const height = attrs.height || 0;
        const verticalOffset =
          attrs.verticalOffset && height > 0 && Math.abs(attrs.verticalOffset) > height * 2
            ? 0
            : attrs.verticalOffset;

        // Only update styles if values actually changed to avoid unnecessary DOM updates
        const newWidth = attrs.width ? `${attrs.width.toFixed(2)}px` : "";
        const newHeight = attrs.height ? `${attrs.height.toFixed(2)}px` : "";
        const newLineHeight = verticalOffset ? `${(verticalOffset * 2).toFixed(2)}px` : "";
        const newLeft = attrs.indentation ? `${attrs.indentation.toFixed(2)}px` : "";

        if (dom.style.width !== newWidth) {
          dom.style.width = newWidth;
        }
        if (dom.style.height !== newHeight) {
          dom.style.height = newHeight;
        }
        if (dom.style.lineHeight !== newLineHeight) {
          dom.style.lineHeight = newLineHeight;
        }
        if (dom.style.left !== newLeft) {
          dom.style.left = newLeft;
        }
      };

      dom.className =
        "expr-placeholder inline-block relative sm:left-0! rounded-5 bg-gray-50 dark:bg-gray-950 before:content-zero-space min-w-[1px]";
      updatePlaceholderStyles(node.attrs as InlineNodeAttrs);

      const cancelObserve = scheduleOnFrame(() => {
        storage.observer?.observe(dom);
      });

      const placeholder: NodeView = {
        dom,
        update: (newNode, dec) => {
          if (newNode.type !== node.type) {
            return false;
          }

          const isActiveNext = getIsNodeCursored(newNode.attrs.nodeId as NodeId, doc);

          const isEager = dec.some((d) => (d.spec as LazyDecSpec).eager === true);
          if (isEager || isActiveNext) {
            return false;
          }

          // Update placeholder styles when attributes change
          updatePlaceholderStyles(newNode.attrs as InlineNodeAttrs);

          // Normal updates
          return true;
        },
        destroy: () => {
          cancelObserve();
          storage.observer?.unobserve(dom);
          storage.getPosMap.delete(dom);
        },
      };

      return withMathMlAnnotation(placeholder, node, doc);
    };
  },

  onDestroy() {
    this.storage.observer?.disconnect();
    this.storage.scrollCleanup?.();
    this.storage.getPosMap.clear();
  },

  addCommands() {
    return {
      insertInlineExpression:
        (inlineNode?: InlineNodeModel, pos?: number, props?: WrapperNodeConstructorProps) =>
        ({ chain }) => {
          const { doc } = this.options;
          const nodeId = createWrapperNode(doc, inlineNode, props);

          let ch = chain();

          if (pos) {
            ch = ch.insertContentAt(pos, {
              type: this.name,
              attrs: { nodeId },
            });
          } else {
            ch = ch.insertContent({
              type: this.name,
              attrs: { nodeId },
            });
          }

          return ch
            .command(({ tr, dispatch }) => {
              if (dispatch) {
                // cursor is currently placed after inserted node
                const selPos = Math.max(tr.selection.anchor - 1, 0);
                const selection = NodeSelection.create(tr.doc, selPos);

                tr.setMeta(lazyPluginKey, { addEager: [selPos] });

                if (!pos) {
                  tr.setSelection(selection);
                }
              }

              return true;
            })
            .run();
        },

      updateInlineExpression:
        (pos: number, newAttrs: Partial<InlineNodeAttrs>) =>
        ({ tr, dispatch, state }) => {
          const node = state.doc.nodeAt(pos);
          if (!node || node.type.name !== this.name) {
            return false;
          }

          if (dispatch) {
            const { selection } = state;
            const wasNodeSelected = selection instanceof NodeSelection && selection.from === pos;

            // this update breaks undo stack because resize observer triggers update too late
            if (!tr.docChanged) {
              // treat transaction as internal so that it doesn't trigger suggestions showing up
              tr.setMeta("implicit", true);
              tr.setMeta("addToHistory", false);
            }

            tr.setNodeMarkup(pos, undefined, {
              ...node.attrs,
              ...newAttrs,
            });

            // Recreate NodeSelection if this is the currently selected node because NodeSelection breaks
            // after setNodeMarkup and it breaks copying of the node content.
            if (wasNodeSelected) {
              const newSelection = NodeSelection.create(tr.doc, pos);
              tr.setSelection(newSelection);
            }
          }

          return true;
        },
    };
  },
});
